import { useState } from 'react';

export default function LogicalFramework() {
  const [activeTab, setActiveTab] = useState<'problem' | 'solution'>('problem');

  return (
    <section id="logical-framework" className="py-10 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12 md:mb-20">
          <div className="inline-flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#1B5E20] flex items-center justify-center">
              <i className="ri-mind-map text-xl md:text-2xl text-white"></i>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B5E20]">الإطار المنطقي</h2>
          </div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            تحليل شامل للمشكلة والحلول الاستراتيجية المقترحة
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12 md:mb-16">
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            <button
              onClick={() => setActiveTab('problem')}
              className={`px-6 md:px-8 py-2.5 md:py-3 rounded-full font-bold text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'problem'
                  ? 'bg-[#1B5E20] text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-alert-line ml-2"></i>
              شجرة المشكلة
            </button>
            <button
              onClick={() => setActiveTab('solution')}
              className={`px-6 md:px-8 py-2.5 md:py-3 rounded-full font-bold text-sm md:text-base transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'solution'
                  ? 'bg-[#1B5E20] text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <i className="ri-lightbulb-line ml-2"></i>
              شجرة البدائل
            </button>
          </div>
        </div>

        {/* Problem Tree */}
        {activeTab === 'problem' && (
          <div className="space-y-8 md:space-y-12">
            {/* Effects */}
            <div className="space-y-4 md:space-y-6">
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-bold text-[#D32F2F] mb-2">الآثار</h3>
                <div className="w-16 h-1 bg-[#D32F2F] mx-auto rounded-full"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-arrow-down-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">انخفاض الأثر</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    ضعف تأثير البرامج الشبابية على المستفيدين
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-funds-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">هدر الموارد</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    إهدار الموارد المالية والبشرية في برامج غير فعالة
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-time-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">ضعف الاستدامة</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    عدم استمرارية البرامج وضعف تأثيرها طويل المدى
                  </p>
                </div>
              </div>
            </div>

            {/* Arrows Down */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-2">
                <i className="ri-arrow-up-line text-4xl md:text-5xl text-[#D32F2F]"></i>
                <i className="ri-arrow-up-line text-4xl md:text-5xl text-[#D32F2F] -mt-6"></i>
              </div>
            </div>

            {/* Main Problem */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-[#D32F2F] to-[#B71C1C] rounded-2xl md:rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-[#D32F2F]">
                <div className="text-center space-y-4 md:space-y-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-error-warning-fill text-4xl md:text-5xl text-[#D32F2F]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                    المشكلة الرئيسية
                  </h3>
                  <p className="text-lg md:text-xl text-white/95 leading-relaxed">
                    ضعف مواءمة البرامج الشبابية مع الاحتياجات الحقيقية
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                      <p className="text-white font-medium text-sm md:text-base">ضعف مخرجات برامج التمكين</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                      <p className="text-white font-medium text-sm md:text-base">الهدر في تمويل برامج غير مبنية على دراسة احتياج</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Arrows Up */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-2">
                <i className="ri-arrow-down-line text-4xl md:text-5xl text-[#D32F2F]"></i>
                <i className="ri-arrow-down-line text-4xl md:text-5xl text-[#D32F2F] -mt-6"></i>
              </div>
            </div>

            {/* Causes */}
            <div className="space-y-4 md:space-y-6">
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-bold text-[#D32F2F] mb-2">الأسباب</h3>
                <div className="w-16 h-1 bg-[#D32F2F] mx-auto rounded-full"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-database-2-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">غياب البيانات</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    عدم توفر بيانات دقيقة عن احتياجات الشباب الفعلية
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-links-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">ضعف التنسيق</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    محدودية التنسيق بين الجهات العاملة مع الشباب
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D32F2F] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-voice-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#D32F2F] mb-3 text-center">محدودية المشاركة</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    ضعف مشاركة الشباب في تصميم البرامج الموجهة لهم
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Solution Tree */}
        {activeTab === 'solution' && (
          <div className="space-y-8 md:space-y-12">
            {/* Strategic Alternative */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-2xl md:rounded-3xl p-8 md:p-12 shadow-2xl border-4 border-[#1B5E20]">
                <div className="text-center space-y-4 md:space-y-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-lightbulb-flash-fill text-4xl md:text-5xl text-[#1B5E20]"></i>
                  </div>
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                    البديل الاستراتيجي
                  </h3>
                  <p className="text-lg md:text-xl text-white/95 leading-relaxed">
                    ملتقى تشاركي تشخيصي
                  </p>
                  <p className="text-base md:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
                    منصة وطنية شاملة تجمع جميع الأطراف المعنية لتشخيص الاحتياجات وبناء الحلول بشكل تشاركي
                  </p>
                </div>
              </div>
            </div>

            {/* Arrows Down */}
            <div className="flex justify-center">
              <div className="flex flex-col items-center gap-2">
                <i className="ri-arrow-down-line text-4xl md:text-5xl text-[#1B5E20]"></i>
                <i className="ri-arrow-down-line text-4xl md:text-5xl text-[#1B5E20] -mt-6"></i>
              </div>
            </div>

            {/* Sub Alternatives */}
            <div className="space-y-4 md:space-y-6">
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-bold text-[#1B5E20] mb-2">البدائل الفرعية</h3>
                <div className="w-16 h-1 bg-[#1B5E20] mx-auto rounded-full"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-bar-chart-box-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20] mb-3 text-center">جمع وتحليل البيانات</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    جمع البيانات النوعية وتحليلها بشكل علمي ممنهج
                  </p>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <ul className="space-y-2 text-xs md:text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>أوراق عمل علمية متخصصة</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>ورش عمل تطبيقية</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>استبيانات ومقابلات</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-team-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20] mb-3 text-center">شراكات وتكامل</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    بناء شراكات فعالة بين جميع الأطراف المعنية
                  </p>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <ul className="space-y-2 text-xs md:text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>الجمعيات الشبابية</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>الجهات الحكومية</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>القطاع الخاص</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl md:rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg hover:shadow-xl transition-all duration-300 group">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-file-list-3-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20] mb-3 text-center">توصيات ومتابعة</h4>
                  <p className="text-sm md:text-base text-gray-600 text-center leading-relaxed">
                    توصيات عملية قابلة للتنفيذ مع آليات متابعة
                  </p>
                  <div className="mt-4 pt-4 border-t border-green-200">
                    <ul className="space-y-2 text-xs md:text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>مؤشر احتياجات الشباب</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>خطط تنفيذية واضحة</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-[#1B5E20] mt-0.5"></i>
                        <span>آليات قياس الأثر</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Expected Impact */}
            <div className="mt-12 md:mt-16 bg-gradient-to-br from-[#F5F5DC] to-white rounded-2xl md:rounded-3xl p-8 md:p-12 shadow-xl border-2 border-[#D4AF37]">
              <h3 className="text-2xl md:text-3xl font-bold text-[#1B5E20] mb-6 md:mb-8 text-center">الأثر المتوقع</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-arrow-up-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20]">زيادة الأثر</h4>
                  <p className="text-sm md:text-base text-gray-600">برامج أكثر فعالية ومواءمة</p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-money-dollar-circle-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20]">كفاءة الموارد</h4>
                  <p className="text-sm md:text-base text-gray-600">استثمار أمثل للموارد المتاحة</p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1B5E20] rounded-full flex items-center justify-center mx-auto">
                    <i className="ri-plant-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-lg md:text-xl font-bold text-[#1B5E20]">الاستدامة</h4>
                  <p className="text-sm md:text-base text-gray-600">حلول مستدامة طويلة المدى</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
